import { useEffect, useId, useState } from "react";
import { ImageOff } from "lucide-react";
import type {
  ImageLabeledGraphicData,
  StoryboardFile,
} from "@/types/storyboard";
import { CourseMarkdown } from "@/components/storyboard/CourseMarkdown";
import { generatedCoordinate } from "@/components/storyboard/image/utils/hotspotUtils";
import HotspotPin from "@/components/storyboard/image/HotspotPin";
import HotspotCard from "@/components/storyboard/image/HotspotCard";
import { useInViewOnce } from "@/hooks/useInViewOnce";

interface ImageLabeledGraphicProps {
  description: ImageLabeledGraphicData;
  file?: StoryboardFile;
}

const ImageLabeledGraphic = ({
  description,
  file,
}: ImageLabeledGraphicProps) => {
  const [activeHotspot, setActiveHotspot] = useState<number | null>(null);
  // One card at a time, so one id is enough — but several graphics can share a
  // page, so it has to be unique per instance.
  const cardId = useId();
  const image = file;
  // One observer for the whole stage: the pins sit within a 640px box, so they
  // come into view together.
  const { ref: stageRef, inView: stageSeen } = useInViewOnce<HTMLDivElement>();

  // Every hotspot with the coordinates it is drawn at: authored ones keep
  // theirs, and the rest - which only the placeholder stage renders - get a
  // generated pair.
  const placedHotspots = description.hotspots.map((hotspot, index) => ({
    hotspot,
    index,
    x: hotspot.x ?? generatedCoordinate(`${index}:${hotspot.title}`, 17),
    y: hotspot.y ?? generatedCoordinate(`${index}:${hotspot.title}`, 91),
  }));

  const setActiveHotspots = (e: React.SyntheticEvent, index: number) => {
    e.stopPropagation();
    setActiveHotspot(activeHotspot === index ? null : index);
  };

  // Escape closes the open card, the way the pointer leaving the pin does.
  useEffect(() => {
    if (activeHotspot === null) return;
    const dismissOnEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") setActiveHotspot(null);
    };
    window.addEventListener("keydown", dismissOnEscape);
    return () => window.removeEventListener("keydown", dismissOnEscape);
  }, [activeHotspot]);

  // At most one card is open, and it hangs off the stage rather than off its
  // pin, so both stages render this one element instead of every pin carrying
  // a card of its own. A hotspot without coordinates never gets a pin on the
  // image stage, so it can never be the open one there either.
  const openHotspot = placedHotspots.find(
    ({ index }) => index === activeHotspot
  );
  const openCard = openHotspot && (
    <HotspotCard
      id={cardId}
      hotspot={openHotspot.hotspot}
      x={openHotspot.x}
      y={openHotspot.y}
    />
  );

  return (
    <div className="prose max-w-full">
      <h2>{description.title}</h2>

      {description.introduction && (
        <div className="prose max-w-full">
          <CourseMarkdown>{description.introduction}</CourseMarkdown>
        </div>
      )}

      {image ? (
        <div
          ref={stageRef}
          // Capped on the wrapper, not the img: hotspots are positioned in
          // percentages against this box, so capping the image inside a wider
          // wrapper would leave every pin misaligned. 640px matches the inline
          // markdown images and Content Factory's editor.
          className="relative my-4 max-w-160"
        >
          <button
            type="button"
            tabIndex={-1}
            className="absolute inset-0 z-[1] cursor-default"
            aria-label="Hotspot schließen"
            onClick={() => setActiveHotspot(null)}
            onMouseOver={() => setActiveHotspot(null)}
            onFocus={() => setActiveHotspot(null)}
          />
          <img
            src={image.url}
            alt={description.title}
            className="pointer-events-none relative z-0 w-full h-auto rounded-lg"
          />

          {description.hotspots.map((hotspot, index) =>
            hotspot.x == null || hotspot.y == null ? null : (
              <HotspotPin
                key={index}
                index={index}
                title={hotspot.title}
                cardId={cardId}
                x={hotspot.x}
                y={hotspot.y}
                isActive={activeHotspot === index}
                pulse={stageSeen}
                onActivate={setActiveHotspots}
              />
            )
          )}

          {openCard}
        </div>
      ) : (
        <div
          ref={stageRef}
          // No image was attached, so the pins are scattered over a neutral
          // stage. Same box and cap as the real image so the block keeps its
          // shape; the authored position text moves into each pin's popover,
          // since a generated coordinate says nothing about where the hotspot
          // actually belongs.
          data-testid="labeled-graphic-placeholder"
          className="relative my-4 max-w-160 aspect-video rounded-lg bg-muted flex items-center justify-center"
        >
          <button
            type="button"
            tabIndex={-1}
            className="absolute inset-0 z-[1] cursor-default"
            aria-label="Hotspot schließen"
            onClick={() => setActiveHotspot(null)}
            onMouseOver={() => setActiveHotspot(null)}
            onFocus={() => setActiveHotspot(null)}
          />
          <ImageOff
            aria-hidden
            className="pointer-events-none relative z-0 w-10 h-10 text-muted-foreground/40"
          />

          {placedHotspots.map(({ hotspot, index, x, y }) => (
            <HotspotPin
              key={index}
              index={index}
              title={hotspot.title}
              cardId={cardId}
              x={x}
              y={y}
              isActive={activeHotspot === index}
              pulse={stageSeen}
              onActivate={setActiveHotspots}
            />
          ))}

          {openCard}
        </div>
      )}
    </div>
  );
};

export default ImageLabeledGraphic;
